import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,

  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,

  Security as SecurityIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Webhook as WebhookIcon,
  Key as KeyIcon
} from '@mui/icons-material';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
// import { ptBR } from 'date-fns/locale';

import { externalApiService } from '../services/externalApiService';
import { agentsService } from '../services/agentsService';
import { ROUTES, EXTERNAL_API } from '../constants';

const CreateApiKey = () => {
  const navigate = useNavigate();
  const { keyId } = useParams();
  const isEdit = Boolean(keyId);

  // State
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [activeStep, setActiveStep] = useState(0);


  // Form data
  const [formData, setFormData] = useState({
    key_name: '',
    allowed_agents: [],
    allowed_channels: ['api'],
    ip_whitelist: [],
    expires_at: null,
    webhook_url: '',
    webhook_secret: '',
    is_active: true
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Steps para o stepper
  const steps = [
    {
      label: 'Informações Básicas',
      description: 'Nome e configurações principais',
      icon: <KeyIcon />
    },
    {
      label: 'Permissões',
      description: 'Agentes e canais permitidos',
      icon: <SecurityIcon />
    },
    {
      label: 'Configurações Avançadas',
      description: 'IP whitelist, expiração e webhook',
      icon: <SettingsIcon />
    }
  ];

  // Load initial data
  useEffect(() => {
    loadInitialData();
    if (isEdit) {
      loadApiKey();
    }
  }, [keyId]);

  // Auto-clear messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load available agents and channels in parallel
      const [agentsResponse, channelsResponse] = await Promise.all([
        agentsService.getAgents(),
        externalApiService.getAvailableChannels()
      ]);

      setAvailableAgents(agentsResponse || []);
      setAvailableChannels(channelsResponse.channels || EXTERNAL_API.CHANNELS);
    } catch (err) {
      console.error('Erro ao carregar dados iniciais:', err);
      setError('Erro ao carregar dados. Tente recarregar a página.');
    } finally {
      setLoading(false);
    }
  };

  const loadApiKey = async () => {
    try {
      setLoading(true);
      const apiKey = await externalApiService.getApiKey(keyId);
      
      setFormData({
        key_name: apiKey.key_name || '',
        allowed_agents: apiKey.allowed_agents || [],
        allowed_channels: apiKey.allowed_channels || ['api'],
        ip_whitelist: apiKey.ip_whitelist || [],
        expires_at: apiKey.expires_at ? new Date(apiKey.expires_at) : null,
        webhook_url: apiKey.webhook_url || '',
        webhook_secret: apiKey.webhook_secret || '',
        is_active: apiKey.is_active ?? true
      });
    } catch (err) {
      console.error('Erro ao carregar API Key:', err);
      setError('Erro ao carregar API Key. Verifique se ela existe.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Nome obrigatório
    if (!formData.key_name?.trim()) {
      newErrors.key_name = 'Nome é obrigatório';
    } else if (formData.key_name.length < 3) {
      newErrors.key_name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (formData.key_name.length > 100) {
      newErrors.key_name = 'Nome deve ter no máximo 100 caracteres';
    }

    // Pelo menos um canal
    if (!formData.allowed_channels || formData.allowed_channels.length === 0) {
      newErrors.allowed_channels = 'Selecione pelo menos um canal';
    }

    // Validar IPs
    if (formData.ip_whitelist && formData.ip_whitelist.length > 0) {
      const ipValidation = externalApiService.validateIpWhitelist(formData.ip_whitelist);
      if (!ipValidation.valid) {
        newErrors.ip_whitelist = ipValidation.errors.join('; ');
      }
    }

    // Validar webhook URL
    if (formData.webhook_url?.trim()) {
      try {
        new URL(formData.webhook_url);
      } catch {
        newErrors.webhook_url = 'URL do webhook inválida';
      }
    }

    // Validar data de expiração
    if (formData.expires_at && formData.expires_at <= new Date()) {
      newErrors.expires_at = 'Data de expiração deve ser no futuro';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Corrija os erros no formulário antes de continuar.');
      return;
    }

    try {
      setSaveLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        expires_at: formData.expires_at ? formData.expires_at.toISOString() : null,
        ip_whitelist: formData.ip_whitelist.filter(ip => ip.trim())
      };

      let result;
      if (isEdit) {
        result = await externalApiService.updateApiKey(keyId, submitData);
        setSuccessMessage('API Key atualizada com sucesso!');
      } else {
        result = await externalApiService.createApiKey(submitData);
        setGeneratedApiKey(result.api_key || result.api_key_secret);
        setSuccessMessage('API Key criada com sucesso! Copie e guarde a chave secreta.');
        setShowApiKey(true);
        // NÃO navegar automaticamente na criação - usuário precisa copiar a key
        return;
      }

      // Apenas navegar automaticamente na edição
      setTimeout(() => {
        navigate(ROUTES.EXTERNAL_API);
      }, 2000);
      
    } catch (err) {
      console.error('Erro ao salvar API Key:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar API Key. Tente novamente.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleIpWhitelistChange = (value) => {
    const ips = value.split('\n').map(ip => ip.trim()).filter(ip => ip);
    handleInputChange('ip_whitelist', ips);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage('Copiado para a área de transferência!');
    } catch (err) {
      console.error('Erro ao copiar:', err);
      setError('Erro ao copiar. Selecione e copie manualmente.');
    }
  };

  const getChannelLabel = (channel) => {
    const channelMap = {
      api: 'API',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      discord: 'Discord',
      slack: 'Slack',
      teams: 'Microsoft Teams',
      webhook: 'Webhook'
    };
    return channelMap[channel] || channel;
  };

  const getStepIcon = (stepIndex) => {
    return steps[stepIndex]?.icon || <KeyIcon />;
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, margin: '0 auto', p: 3 }}> {/* Replaced LocalizationProvider temporarily */}
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.EXTERNAL_API)}
            sx={{ mb: 2 }}
          >
            Voltar para API Externa
          </Button>
          
          <Typography variant="h4" gutterBottom>
            {isEdit ? 'Editar API Key' : 'Nova API Key'}
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            {isEdit 
              ? 'Atualize as configurações da API Key existente.'
              : 'Crie uma nova API Key para integração com aplicações externas.'
            }
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Generated API Key Display */}
        {generatedApiKey && (
          <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔑 API Key Criada com Sucesso!
              </Typography>
              <Typography variant="body2" gutterBottom>
                Esta chave será exibida apenas uma vez. Copie e guarde em local seguro:
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mt: 2,
                p: 2,
                bgcolor: 'rgba(0,0,0,0.1)',
                borderRadius: 1
              }}>
                <TextField
                  value={generatedApiKey}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    type: showApiKey ? 'text' : 'password',
                    endAdornment: (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => setShowApiKey(!showApiKey)}
                        >
                          {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(generatedApiKey)}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Box>
                    )
                  }}
                />
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                onClick={() => navigate(ROUTES.EXTERNAL_API)}
                variant="contained"
                color="inherit"
              >
                Ir para Lista de API Keys
              </Button>
            </CardActions>
          </Card>
        )}

        {/* Form */}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 1: Informações Básicas */}
              <Step>
                <StepLabel 
                  icon={getStepIcon(0)}
                  onClick={() => setActiveStep(0)}
                  sx={{ cursor: 'pointer' }}
                >
                  {steps[0].label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {steps[0].description}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        label="Nome da API Key"
                        value={formData.key_name}
                        onChange={(e) => handleInputChange('key_name', e.target.value)}
                        fullWidth
                        required
                        error={!!errors.key_name}
                        helperText={errors.key_name || 'Nome identificador para a API Key (ex: WhatsApp Bot, Sistema CRM)'}
                        placeholder="Ex: Integração WhatsApp"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.is_active}
                            onChange={(e) => handleInputChange('is_active', e.target.checked)}
                          />
                        }
                        label="API Key ativa"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      variant="contained" 
                      onClick={() => setActiveStep(1)}
                      disabled={!!errors.key_name}
                    >
                      Continuar
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Permissões */}
              <Step>
                <StepLabel 
                  icon={getStepIcon(1)}
                  onClick={() => setActiveStep(1)}
                  sx={{ cursor: 'pointer' }}
                >
                  {steps[1].label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {steps[1].description}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Tooltip 
                          title="Selecione através de quais canais esta API Key pode ser usada. API é obrigatório para integrações básicas. WhatsApp, Telegram e Web permitem integrações específicas."
                          placement="top"
                          arrow
                        >
                        <FormControl fullWidth error={!!errors.allowed_channels}>
                          <InputLabel>Canais Permitidos *</InputLabel>
                          <Select
                            multiple
                            label="Canais Permitidos *"
                            value={formData.allowed_channels}
                            onChange={(e) => handleInputChange('allowed_channels', e.target.value)}
                            input={<OutlinedInput label="Canais Permitidos *" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={getChannelLabel(value)} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {availableChannels.map((channel) => (
                              <MenuItem key={channel} value={channel}>
                                {getChannelLabel(channel)}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.allowed_channels && (
                            <Typography variant="caption" color="error">
                              {errors.allowed_channels}
                            </Typography>
                          )}
                        </FormControl>
                        </Tooltip>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box>

                        <Tooltip 
                          title="Selecione quais agentes podem ser acessados com esta API Key. Deixe vazio para permitir todos os agentes da empresa."
                          placement="top"
                          arrow
                        >
                        <FormControl fullWidth>
                          <InputLabel>Agentes Permitidos</InputLabel>
                          <Select
                            multiple
                            label="Agentes Permitidos"
                            value={formData.allowed_agents}
                            onChange={(e) => handleInputChange('allowed_agents', e.target.value)}
                            input={<OutlinedInput label="Agentes Permitidos" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {availableAgents.map((agent) => (
                              <MenuItem key={agent.id} value={agent.name}>
                                {agent.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button onClick={() => setActiveStep(0)}>
                      Voltar
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={() => setActiveStep(2)}
                      disabled={!!errors.allowed_channels}
                    >
                      Continuar
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Configurações Avançadas */}
              <Step>
                <StepLabel 
                  icon={getStepIcon(2)}
                  onClick={() => setActiveStep(2)}
                  sx={{ cursor: 'pointer' }}
                >
                  {steps[2].label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {steps[2].description}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Box>

                        <Tooltip 
                          title="Liste os IPs ou redes (CIDR) que podem usar esta API Key. Deixe vazio para permitir qualquer IP. Exemplo: 192.168.1.100, 10.0.0.0/24"
                          placement="top"
                          arrow
                        >
                        <TextField
                        label="IP Whitelist"
                        value={formData.ip_whitelist.join('\n')}
                        onChange={(e) => handleIpWhitelistChange(e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                        error={!!errors.ip_whitelist}
                        helperText={errors.ip_whitelist || 'Um IP por linha. Ex: 192.168.1.100 ou 10.0.0.0/24'}
                        placeholder="192.168.1.100&#10;10.0.0.0/24"
                        />
                        </Tooltip>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Tooltip 
                        title="A API Key será desativada automaticamente após esta data. Deixe vazio para não expirar."
                        placement="top"
                        arrow
                      >
                      <TextField
                        label="Data de Expiração"
                        type="datetime-local"
                        value={formData.expires_at ? formData.expires_at.toISOString().slice(0, 16) : ''}
                        onChange={(e) => handleInputChange('expires_at', e.target.value ? new Date(e.target.value) : null)}
                        fullWidth
                        error={!!errors.expires_at}
                        helperText={errors.expires_at || 'Deixe vazio para não expirar'}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      </Tooltip>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box>

                        <Tooltip 
                          title="O webhook é uma URL do seu sistema que receberá notificações automáticas sempre que sua API Key for usada. Por exemplo: quando alguém conversar com seu agente via WhatsApp, você receberá um POST com detalhes da conversa, tokens usados, custos, etc. Útil para: monitoramento em tempo real, auditoria de uso, integração com sistemas de billing próprios, logs personalizados."
                          placement="top"
                          arrow
                        >
                        <TextField
                        label="Webhook URL"
                        value={formData.webhook_url}
                        onChange={(e) => handleInputChange('webhook_url', e.target.value)}
                        fullWidth
                        error={!!errors.webhook_url}
                        helperText={errors.webhook_url || 'URL que receberá notificações de uso da API Key (opcional)'}
                        placeholder="https://seu-sistema.com/webhook/api-externa"
                        />
                        </Tooltip>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Tooltip 
                        title="Secret para validar que as notificações webhook realmente vieram do nosso sistema. Use esta chave para verificar a assinatura HMAC-SHA256 que enviamos no header X-Webhook-Signature."
                        placement="top"
                        arrow
                      >
                      <TextField
                        label="Webhook Secret"
                        value={formData.webhook_secret}
                        onChange={(e) => handleInputChange('webhook_secret', e.target.value)}
                        fullWidth
                        type="password"
                        helperText="Chave secreta para validar a autenticidade das notificações webhook (opcional)"
                        placeholder="ChaveSecreta123"
                      />
                      </Tooltip>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                    <Button onClick={() => setActiveStep(1)}>
                      Voltar
                    </Button>
                    <Button 
                      type="submit"
                      variant="contained"
                      startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={saveLoading || Object.keys(errors).length > 0}
                    >
                      {saveLoading 
                        ? 'Salvando...' 
                        : isEdit 
                          ? 'Atualizar API Key' 
                          : 'Criar API Key'
                      }
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </form>
        </Paper>

        {/* API Key Success Dialog */}
        <Dialog 
          open={showApiKey && generatedApiKey} 
          onClose={() => {}}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle sx={{ 
            bgcolor: 'success.main', 
            color: 'success.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <KeyIcon />
            API Key Criada com Sucesso!
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              ⚠️ <strong>IMPORTANTE:</strong> Esta é a única vez que você verá a API Key completa. 
              Copie e guarde em local seguro agora!
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Sua nova API Key:
            </Typography>
            
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'grey.300',
              mb: 2 
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'monospace', 
                  wordBreak: 'break-all',
                  fontSize: '0.9rem'
                }}
              >
                {generatedApiKey}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Button
                startIcon={<CopyIcon />}
                variant="contained"
                onClick={() => copyToClipboard(generatedApiKey)}
                fullWidth
              >
                Copiar API Key
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              <strong>Próximos passos:</strong>
              <br />• Use esta API Key nas suas integrações
              <br />• Confira a documentação em /docs/api-externa  
              <br />• Monitore o uso na tela de estatísticas
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setShowApiKey(false);
                setGeneratedApiKey(null);
                navigate(ROUTES.EXTERNAL_API);
              }}
              variant="contained"
              color="primary"
            >
              Entendi, ir para listagem
            </Button>
          </DialogActions>
        </Dialog>


      </Box>
  );
};

export default CreateApiKey;
